import React, { Component } from "react";
import { ContractComputeRequest, ContractFinance } from "@eres/ejari-api/Entity/contract";
import _ from "lodash";
import moment from "moment";
import { TenantContractContext, updateTenantContractContext } from "@eres/ejari-core/contexts/TenantContractContext";
import { WebFormFieldProps } from "@eres/forms-web";
import FormSection from "@my-dld/components/Form/FormSection";
import Strings from "@my-dld/assets/strings";
import StateHolder from "@eres/forms/StateHolder";
import DateInputField from "@eres/forms-web/DateInputField";
import { dualDataManager } from "@eres/forms/valuemanager/DualDataManager";
import { IdExtractor, lookupConverter, serverDateConverter } from "@my-dld/utils/LookupUtil";
import FormRow from "@eres/forms-web/FormRow";
import YesNoField from "@eres/forms-web/YesNoField";
import { hideLoading, LoadableDataState, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { ErrorContainer } from "@eres/forms";
import { checkNonEmpty } from "../../utils";
import { defaultErrorManager } from "@eres/forms/errormanager";
import { EjariApi } from "@eres/ejari-api";
import { NonZeroValidator } from "@eres/forms/validator/NonZeroValidator";
import CurrencyInputField from "@eres/forms-web/CurrencyInputField";
import ContractCalculatedValue from "../Contract/ContractCalculatedValue";
import FormAction from "@my-dld/components/Form/FormAction";
import { navigate } from "@my-dld/utils/StoreUtils";
import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import { TenancyContactAction } from "@eres/ejari-core/constants";
import { TCContractInfo } from "@eres/ejari-api/Entity/tenant";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";
import PickerInput from "@eres/forms-web/PickerInput";
import TextInputField from "@eres/forms-web/TextInputField";
import { EjariPropertyUsage, Lookup } from "@eres/ejari-api/Entity";
import { ServerDate } from "@eres/core";
import { populateLookupOnTo, StateConsumer } from "@eres/ejari-api/Utils";
import { EjariLookUp } from "@eres/ejari-api/constants";
import FormMandatory from "../FormMandatory";

interface Props extends RoutedViewProps {

}

interface LookupState {
	Reasons?: Lookup[];
}

interface State extends LoadableDataState, LookupState {
	newData?: TCContractInfo;
	orgData?: TCContractInfo;
	isCalculated?: boolean;
	calculatedValue?: ContractFinance;
	isFromContext?: boolean;
}

class ContractInfo extends Component<Props, State> {
	static contextType = TenantContractContext;
	context!: React.ContextType<typeof TenantContractContext>;

	constructor( props: Props ) {
		super( props );
		this.state = {
			errorContainer: {},
			loadState: "loading",
		};
	}

	private static canShowReason( endDate?: ServerDate ) {
		if ( endDate && !isEmptyStringOrNull( endDate ) ) {
			const end = moment( endDate );
			let today = moment( new Date() );
			return end.isBefore( today );
		}
		return false;
	}

	componentDidMount() {
		this.updateFromContext();
	}

	componentDidUpdate( prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: TCContractInfo ) {
		const od = this.getUpdatedData( prevState );
		const nd = this.getUpdatedData();
		if ( this.isFieldsChanged( od, nd ) ) {
			this.setState( {
				isCalculated: false,
				calculatedValue: undefined
			} );
		}
	}

	render() {
		const filedProps: Partial<WebFormFieldProps<any, any>> = {
			holder: StateHolder.buildStateHolder( this ),
			containerClassName: "col-md-4 form-group",
			valueManager: dualDataManager,
			errorManager: defaultErrorManager,
		};
		const { action } = this.context;
		const data = this.getUpdatedData();
		const { isCalculated, calculatedValue, Reasons } = this.state;
		return <div className="tender-registration-form">
			<FormSection title={ Strings.contract_information }>
				<FormMandatory/>
				<FormRow { ...filedProps }>
					<DateInputField
						required={ true }
						id={ "ContractStartDate" }
						label={ Strings.contract_start_date }
						valueConverter={ serverDateConverter }
						extraOpts={ {
							maxDate: data.ContractEndDate?.asDate()
						} }
						extraInputClassName={ "data-picker-icon" }
						disabled={ action === TenancyContactAction.RENEW_CONTRACT }/>
					<DateInputField
						required={ true }
						id={ "ContractEndDate" }
						label={ Strings.contract_end_date }
						valueConverter={ serverDateConverter }
						extraInputClassName={ "data-picker-icon" }
						extraOpts={ {
							minDate: data.ContractStartDate?.asDate()
						} }/>
					<CurrencyInputField
						required={ true }
						id={ "ContractAmount" }
						label={ Strings.contract_amount }
						valueValidators={ NonZeroValidator }/>
					<CurrencyInputField
						required={ action === TenancyContactAction.RENEW_CONTRACT }
						id={ "SecurityDeposit" }
						label={ Strings.security_deposit }/>
				</FormRow>
				<FormRow { ...filedProps } containerClassName="col-md-6 form-group">
					<YesNoField
						required={ true }
						label={ Strings.grace_period }
						id={ "IsGracePeriod" }
						yesLabel={ Strings.yes }
						noLabel={ Strings.no }
						containerClassName={ "" }/>
				</FormRow>
				{ data.ShowReason && <FormRow { ...filedProps }>
					<PickerInput
						required={ true }
						id={ "RegReasonId" }
						label={ Strings.registration_reason }
						isLoading={ Reasons === undefined }
						items={ Reasons }
						toIdentifier={ IdExtractor }
						valueConverter={ lookupConverter }/>
					{ data.RegReasonId === 5 && <TextInputField
						required={ true }
						id={ "RegistrationReason" }
						label={ Strings.registration_reason }/> }
				</FormRow> }
				{ data.IsGracePeriod === true &&
					<FormRow { ...filedProps }>
						<DateInputField
							required={ true }
							label={ Strings.contract_start_date }
							id={ "GraceStartDate" }
							valueConverter={ serverDateConverter }
							extraOpts={ {
								minDate: data.ContractStartDate?.asDate(),
								maxDate: this.getMaxGraceStartDate( data ),
							} }/>
						<DateInputField
							required={ true }
							label={ Strings.contract_end_date }
							id={ "GraceEndDate" }
							valueConverter={ serverDateConverter }
							extraOpts={ {
								minDate: this.getMinGraceEndDate( data ),
								maxDate: data.ContractEndDate?.asDate(),
							} }/>
					</FormRow> }
				{ isCalculated && <ContractCalculatedValue data={ calculatedValue! }/> }
			</FormSection>
			{ !isCalculated && <FormRow { ...filedProps }>
				<FormAction
					groupClassName={ "text-center" }
					primaryActionLabel={ Strings.calculate_total }
					onPrimaryAction={ this.onCalculate }
					secondaryActionLabel={ Strings.back }
					onSecondaryAction={ this.onBack }
					actionContainerClassName={ "col-md-12" }
				/>
			</FormRow> }
			{ isCalculated && <FormRow { ...filedProps }>
				<RenderActions
					action={ action! } onContinue={ this.onContinue }
					onSave={ this.onSave } onBack={ this.onBack }/>
			</FormRow> }
		</div>;
	}

	private isFieldsChanged( oldData: TCContractInfo, newData: TCContractInfo ) {
		if ( !this.state.isFromContext && newData && oldData ) {
			const matchSD = newData.ContractStartDate !== oldData.ContractStartDate;
			const matchED = newData.ContractEndDate !== oldData.ContractEndDate;
			const matchCV = newData.ContractAmount !== oldData.ContractAmount;
			const matchGP = newData.IsGracePeriod !== oldData.IsGracePeriod;
			const matchGPSD = newData.GraceStartDate !== oldData.GraceStartDate;
			const matchGPED = newData.GraceEndDate !== oldData.GraceEndDate;
			return ( matchSD || matchED || matchCV || matchGP || matchGPSD || matchGPED );
		} else {
			this.setState( { isFromContext: false } );
			return false;
		}
	}

	private updateFromContext() {
		const { contract, contractInfo, action } = this.context;
		const start = new Date();
		const orgFinance: TCContractInfo = {
			ContractStartDate: start.asServerString(),
			ContractEndDate: moment( start ).add( 1, "years" ).add( -1, "days" ).toDate().asServerString(),
			IsGracePeriod: false,
			ShowReason: false,
		};
		if ( contractInfo == undefined && contract ) {
			orgFinance.ContractStartDate = contract.ContractStartDate;
			orgFinance.ContractEndDate = contract.ContractEndDate;
			orgFinance.ContractAmount = contract.ContractAmount;
			orgFinance.SecurityDeposit = contract.SecurityDeposit;
			if ( action === TenancyContactAction.RENEW_CONTRACT ) {
				const sd = moment( contract.ContractEndDate ).add( 1, "days" ).toDate().asServerString();
				const ed = moment( sd ).add( 1, "years" ).add( -1, "days" ).toDate().asServerString();
				orgFinance.ContractStartDate = sd;
				orgFinance.ContractEndDate = ed;
				orgFinance.ShowReason = ContractInfo.canShowReason( ed );
			} else if ( action === TenancyContactAction.UPDATE_CONTRACT ) {
				orgFinance.IsGracePeriod = !isEmptyStringOrNull( contract.GraceStartDate ) && isEmptyStringOrNull( contract.GraceEndDate );
				orgFinance.GraceStartDate = contract.GraceStartDate;
				orgFinance.GraceEndDate = contract.GraceEndDate;
				orgFinance.ShowReason = ContractInfo.canShowReason( contract.ContractEndDate ) && ( contract.RegReasonId !== undefined );
				orgFinance.RegReasonId = contract.RegReasonId;
				orgFinance.RegistrationReason = contract.RegistrationReason;
			}
		}
		const state = {
			orgData: orgFinance || {},
			newData: contractInfo || {},
			isCalculated: ( contractInfo !== undefined ),
			calculatedValue: contractInfo ? contractInfo.CalculatedValue : undefined,
			isFromContext: ( contractInfo !== undefined ),
		};
		const data: TCContractInfo = {};
		_.merge( data, orgFinance, contractInfo );
		if ( data.ShowReason ) {
			populateLookupOnTo( EjariLookUp.REG_REASON, StateConsumer( this, "Reasons" ) );
		}
		this.setState( state );
	}

	private getUpdatedData = ( state: State = this.state ) => {
		const data: TCContractInfo = {};
		_.merge( data, state.orgData, state.newData );
		return data;
	};

	private getMaxGraceStartDate = ( data: TCContractInfo ) => {
		if ( data.GraceEndDate )
			return data.GraceEndDate.asDate();
		return data.ContractEndDate ? data.ContractEndDate!.asDate() : undefined;
	};

	private getMinGraceEndDate = ( data: TCContractInfo ) => {
		if ( data.GraceStartDate )
			return data.GraceStartDate.asDate();
		return data.ContractStartDate ? data.ContractStartDate!.asDate() : undefined;
	};

	private isValid() {
		const data = this.getUpdatedData();
		const error: ErrorContainer = {};
		let isValid = checkNonEmpty( data, "ContractStartDate", error );
		isValid = checkNonEmpty( data, "ContractEndDate", error ) && isValid;
		isValid = checkNonEmpty( data, "ContractAmount", error ) && isValid;
		if ( this.context.action === TenancyContactAction.RENEW_CONTRACT ) {
			isValid = checkNonEmpty( data, "SecurityDeposit", error ) && isValid;
		}
		if ( data.IsGracePeriod ) {
			isValid = checkNonEmpty( data, "GraceStartDate", error ) && isValid;
			isValid = checkNonEmpty( data, "GraceEndDate", error ) && isValid;
			if ( isValid ) {
				let ms = moment( data.ContractStartDate!.asDate() ).startOf( "day" );
				let me = moment( data.ContractEndDate!.asDate() ).startOf( "day" );
				let mgs = moment( data.GraceStartDate!.asDate() ).startOf( "day" );
				let mge = moment( data.GraceEndDate!.asDate() ).startOf( "day" );
				if ( !( ms.isSame( mgs ) || me.isSame( mge ) ) ) {
					isValid = false;
					error.GraceStartDate = Strings.grace_date_validation;
				}
			}
		}
		if ( data.ShowReason ) {
			if ( data.RegReasonId == undefined ) {
				isValid = false;
				error.RegReasonId = Strings.required;
			}
			if ( data.RegReasonId && data.RegReasonId === 5 ) {
				isValid = checkNonEmpty( data, "RegistrationReason", error ) && isValid;
			}
		}
		this.setState( {
			errorContainer: error,
		} );
		return isValid;
	}

	private onCalculate = () => {
		if ( this.isValid() ) {
			const data = this.getUpdatedData();
			const { property } = this.context;
			const req: ContractComputeRequest = {
				StartDate: data.ContractStartDate,
				EndDate: data.ContractEndDate,
				EjariPropertyID: property?.PropertyId,
			};
			if ( data.IsGracePeriod ) {
				req.GraceStartDate = data.GraceStartDate;
				req.GraceEndDate = data.GraceEndDate;
			}
			req.ContractValue = data.ContractAmount;
			req.DiscountTypeId = 1;
			req.DiscountValue = 0;
			showLoading();
			EjariApi.calculateContractValue( req ).then( resp => {
				hideLoading();
				this.setState( {
					isCalculated: true,
					calculatedValue: resp,
				} );
			} ).catch( e => {
				hideLoading();
				showErrorMessage( e );
			} );
		}
	};

	private onContinue = () => {
		const { property, action } = this.context;
		const { calculatedValue } = this.state;
		const data = this.getUpdatedData();
		const contractInfo: TCContractInfo = {
			...data,
			CalculatedValue: calculatedValue,
		};
		let isCommercial = false;
		if ( property && property.EjariPropertyUsageId && property.EjariPropertyUsageId !== EjariPropertyUsage.RESIDENTIAL ) {
			isCommercial = true;
		}
		updateTenantContractContext( { contractInfo: contractInfo, isCommercial: isCommercial } );
		if ( action === TenancyContactAction.NEW_CONTRACT ) {
			navigate( "moveout" );
		} else {
			navigate( isCommercial ? "commercial" : "documents" );
		}
	};

	private onSave = () => {
		if ( this.isValid() ) {
			let { contract } = this.context;
			const { calculatedValue } = this.state;
			const data = this.getUpdatedData();
			const contractInfo: TCContractInfo = {
				...data,
				CalculatedValue: calculatedValue,
			};
			contract = {
				...contract,
				ContractStartDate: data.ContractStartDate,
				ContractEndDate: data.ContractEndDate,
				SecurityDeposit: data.SecurityDeposit,
				GraceStartDate: data.GraceStartDate,
				GraceEndDate: data.GraceEndDate,
				ContractAmount: Number( data.ContractAmount ),
				RegReasonId: data.RegReasonId,
				RegistrationReason: data.RegistrationReason,
			};
			updateTenantContractContext( { contract: contract, contractInfo: contractInfo, canResubmit: true } );
			this.onBack();
		}
	};

	private onBack = () => {
		const { action } = this.context;
		if ( action === TenancyContactAction.NEW_CONTRACT )
			this.props.history.go( -2 );
		else
			this.props.history.goBack();
	};
}

export function RenderActions( props: {
	action: string, onContinue: () => void, onSave: () => void,
	onBack: () => void, pLabel?: string
} ) {
	const { action, onContinue, onSave, onBack, pLabel } = props;
	return <React.Fragment>
		{ ( action === TenancyContactAction.UPDATE_CONTRACT ) ?
			<FormAction
				groupClassName={ "text-center" }
				primaryActionLabel={ Strings.save }
				onPrimaryAction={ onSave }
				secondaryActionLabel={ Strings.back }
				onSecondaryAction={ onBack }
				actionContainerClassName={ "col-md-12" }/> :
			<FormAction
				groupClassName={ "text-center" }
				primaryActionLabel={ pLabel ? pLabel : Strings.continue }
				onPrimaryAction={ onContinue }
				secondaryActionLabel={ Strings.back }
				onSecondaryAction={ onBack }
				actionContainerClassName={ "col-md-12" }/> }
	</React.Fragment>;
}

export default ContractInfo;
